// src/Pages/Assets/FuturesSection.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography, Tabs, Tab, IconButton, Button, Chip } from '@mui/material';
import { alpha, useTheme, darken } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import SummaryMetrics from './futuresComponentes/SummaryMetrics';
import FiltersBar from './futuresComponentes/FiltersBar';
import PositionsList from './futuresComponentes/PositionsList';
import HistoryList from './futuresComponentes/HistoryList';
import PnLChart from './futuresComponentes/pnlChart';

import {
  BN_YELLOW,
  injectOpenComputed,
  injectHistoryComputed,
} from './futuresComponentes/pnlUtils';

import {
  fetchOpenFutureTrades,
  fetchFutureTradeHistory,
  closeFutureTrade,
  cancelFutureTrade,
} from '../../store/Slices/FuturesTradingSlice';
import { fetchFuturesBalances } from '../../store/Slices/assetSlice';

export default function FuturesSection() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const brand = BN_YELLOW;
  const borderCol = alpha(theme.palette.divider, 0.6);

  const { futuresBalances, coinPrices, spotTradingPairs } = useSelector((s) => s.assets);
  const { openTrades = [], tradeHistory = [], openOrdersLoading, tradeHistoryLoading } = useSelector((s) => s.futures || {});
  const usdtBal = futuresBalances.find(b => b.asset_id?.symbol === 'USDT')?.balance || 0;

  useEffect(() => {
    dispatch(fetchFuturesBalances());
    dispatch(fetchOpenFutureTrades());
    dispatch(fetchFutureTradeHistory());
  }, [dispatch]);

  const pairIdToSymbol = useMemo(() => {
    const map = {};
    for (const p of (spotTradingPairs || [])) {
      const sym = p?.symbol || (p?.base_asset_id?.symbol ? `${p.base_asset_id.symbol}USDT` : undefined);
      if (p?._id && sym) map[String(p._id)] = String(sym).toUpperCase();
    }
    return map;
  }, [spotTradingPairs]);

  const openRows = useMemo(
    () => injectOpenComputed(openTrades, pairIdToSymbol, coinPrices),
    [openTrades, pairIdToSymbol, coinPrices]
  );
  const historyRows = useMemo(
    () => injectHistoryComputed(tradeHistory, pairIdToSymbol),
    [tradeHistory, pairIdToSymbol]
  );

  const totalUnrealized = useMemo(() => openRows.reduce((a, r) => a + (r._pnl || 0), 0), [openRows]);
  const totalIM = useMemo(() => openRows.reduce((a, r) => a + (r._im || 0), 0), [openRows]);
  const equity = useMemo(() => usdtBal + totalUnrealized, [usdtBal, totalUnrealized]);
  const marginRatio = useMemo(
    () => (!equity || equity <= 0) ? 1 : (!totalIM ? 0 : Math.max(0, Math.min(1, totalIM / equity))),
    [equity, totalIM]
  );
  const available = useMemo(() => Math.max(0, usdtBal - totalIM), [usdtBal, totalIM]);

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({ pair: 'ALL', position: 'ALL', margin: 'ALL', type: 'ALL', status: 'ALL', q: '', from: '', to: '' });
  const clearFilters = () => setFilters({ pair: 'ALL', position: 'ALL', margin: 'ALL', type: 'ALL', status: 'ALL', q: '', from: '', to: '' });

  const pairsOpen = useMemo(() => ['ALL', ...Array.from(new Set(openRows.map(r => r._symbol).filter(Boolean)))], [openRows]);
  const pairsHist = useMemo(() => ['ALL', ...Array.from(new Set(historyRows.map(r => r._symbol).filter(Boolean)))], [historyRows]);

  const filterFn = (arr, forHistory = false) => {
    const from = forHistory && filters.from ? new Date(filters.from).getTime() : null;
    const to   = forHistory && filters.to ? new Date(filters.to).getTime() + 86399999 : null;
    const q    = filters.q.trim().toLowerCase();
    return arr.filter((r) => {
      if (filters.pair !== 'ALL' && r._symbol !== filters.pair) return false;
      if (filters.position !== 'ALL' && r.position !== filters.position) return false;
      if (filters.margin !== 'ALL' && r.margin_type !== filters.margin) return false;
      if (filters.type !== 'ALL' && r.order_type !== filters.type) return false;
      if (filters.status !== 'ALL' && r.status !== filters.status) return false;
      if (forHistory) {
        const ts = r.created_at ? new Date(r.created_at).getTime() : (r.updated_at ? new Date(r.updated_at).getTime() : null);
        if (from && (!ts || ts < from)) return false;
        if (to && (!ts || ts > to)) return false;
      }
      if (q) {
        const hay = `${r._symbol} ${r.position} ${r.margin_type} ${r.order_type} ${r.status} ${r._id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  };

  const filteredOpen = useMemo(() => filterFn(openRows, false), [openRows, filters]);
  const filteredHist = useMemo(() => filterFn(historyRows, true), [historyRows, filters]);

  const onClose = (row) => dispatch(closeFutureTrade({ trade_id: row._id })).then(() => dispatch(fetchFuturesBalances()));
  const onCancel = (row) => dispatch(cancelFutureTrade({ trade_id: row._id })).then(() => dispatch(fetchFuturesBalances()));
  const refreshAll = () => {
    dispatch(fetchFuturesBalances());
    dispatch(fetchOpenFutureTrades());
    dispatch(fetchFutureTradeHistory());
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${borderCol}`, backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[1] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Futures Account</Typography>
          <Chip size="small" label="USDT-M" sx={{ bgcolor: alpha(BN_YELLOW, 0.18), color: theme.palette.text.primary, border: `1px solid ${alpha(BN_YELLOW, 0.35)}`, fontWeight: 700 }} />
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={refreshAll} sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary, bgcolor: alpha(BN_YELLOW, 0.12) } }} aria-label="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Button variant="contained" endIcon={<ArrowOutwardIcon />} onClick={() => (window.location.href = '/transfer')}
              sx={{ borderRadius: 2, fontWeight: 800, boxShadow: 'none', bgcolor: BN_YELLOW, color: '#111', '&:hover': { bgcolor: darken(BN_YELLOW, .05), boxShadow: 'none' } }}>
              Transfer
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Summary */}
      <SummaryMetrics brand={BN_YELLOW} equity={equity} usdtBal={usdtBal} totalUnrealized={totalUnrealized} totalIM={totalIM} available={available} marginRatio={marginRatio} />

      {/* PnL Chart */}
      <PnLChart brand={BN_YELLOW} historyRows={historyRows} />

      {/* Tabs */}
      <Paper sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${borderCol}`, backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[1] }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${borderCol}`, mb: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 42 }, '& .MuiTabs-indicator': { background: BN_YELLOW, height: 3, borderRadius: 3 } }}>
          <Tab label="Open Positions" />
          <Tab label="Trade History" />
        </Tabs>

        {/* Filters */}
        {tab === 0 ? (
          <FiltersBar filters={filters} setFilters={setFilters} pairs={pairsOpen} onClear={clearFilters} />
        ) : (
          <FiltersBar filters={filters} setFilters={setFilters} pairs={pairsHist} onClear={clearFilters} showDates />
        )}

        {/* Content with fixed maxHeight + vertical scroll */}
        <Box sx={{ mt: 1 }}>
          {tab === 0 ? (
            <PositionsList rows={filteredOpen} loading={openOrdersLoading} brand={BN_YELLOW} onClose={onClose} onCancel={onCancel} maxHeight="58vh" />
          ) : (
            <HistoryList rows={filteredHist} loading={tradeHistoryLoading} brand={BN_YELLOW} maxHeight="58vh" />
          )}
        </Box>
      </Paper>
    </Box>
  );
}
